import { createContext, ReactElement, useMemo, useContext, useCallback } from 'react';
import useRestaurantData from '../../hooks/useRestaurantData';
import { DiscoveryContentItemInterface } from '../../types/DiscoveryContentItemInterface';
import { getDiscoveryContentItems } from '../../api/discoveryContentItem';
import { removeAtIndex, replaceAtIndex } from '../../utils/general';

interface DiscoveryContentItemsContextInterface {
  hasError: boolean;
  isLoading: boolean;
  discoveryContentItems: DiscoveryContentItemInterface[];
  loadDiscoveryContentItems: Function;
  addDiscoveryContentItem: Function;
  getDiscoveryContentItem: Function;
  removeDiscoveryContentItem: Function;
  updateDiscoveryContentItem: Function;
}

interface DiscoveryContentItemsProviderInterface {
  children: ReactElement;
}

const DiscoveryContentItemsContext = createContext<DiscoveryContentItemsContextInterface>({
  hasError: false,
  isLoading: true,
  discoveryContentItems: null,
  loadDiscoveryContentItems: () => {},
  addDiscoveryContentItem: () => {},
  getDiscoveryContentItem: () => {},
  removeDiscoveryContentItem: () => {},
  updateDiscoveryContentItem: () => {}
});

const DiscoveryContentItemsProvider = ({ children }: DiscoveryContentItemsProviderInterface) => {
  const {
    data: discoveryContentItems,
    setData: setDiscoveryContentItems,
    isLoading,
    hasError,
    load: loadDiscoveryContentItems
  } = useRestaurantData<DiscoveryContentItemInterface[]>(getDiscoveryContentItems);

  const addDiscoveryContentItem = useCallback(
    (discoveryContentItem: DiscoveryContentItemInterface) => {
      const _discoveryContentItems = discoveryContentItems.slice();
      _discoveryContentItems.push({ ...discoveryContentItem } as DiscoveryContentItemInterface);
      setDiscoveryContentItems(_discoveryContentItems);
    },
    [discoveryContentItems, setDiscoveryContentItems]
  );

  const getDiscoveryContentItem = useCallback(
    (discoveryContentID: number): DiscoveryContentItemInterface =>
      discoveryContentItems.find(
        (discoveryContentItem) => discoveryContentItem.discoveryContentID === discoveryContentID
      ),
    [discoveryContentItems]
  );

  const removeDiscoveryContentItem = useCallback(
    (discoveryContentID: number) => {
      const discoveryContentItemIndex: number = discoveryContentItems.findIndex(
        (section) => section.discoveryContentID === discoveryContentID
      );
      setDiscoveryContentItems(removeAtIndex(discoveryContentItems, discoveryContentItemIndex));
    },
    [discoveryContentItems, setDiscoveryContentItems]
  );

  const updateDiscoveryContentItem = useCallback(
    (discoveryContentItem: DiscoveryContentItemInterface) => {
      const discoveryContentIndex: number = discoveryContentItems.findIndex(
        (item) => item.discoveryContentID === discoveryContentItem.discoveryContentID
      );

      setDiscoveryContentItems(
        replaceAtIndex(discoveryContentItems, discoveryContentIndex, {
          ...discoveryContentItems[discoveryContentIndex],
          ...discoveryContentItem
        })
      );
    },
    [discoveryContentItems, setDiscoveryContentItems]
  );

  const providerValue = useMemo(
    () => ({
      hasError,
      isLoading,
      discoveryContentItems,
      loadDiscoveryContentItems,
      addDiscoveryContentItem,
      getDiscoveryContentItem,
      removeDiscoveryContentItem,
      updateDiscoveryContentItem
    }),
    [
      hasError,
      isLoading,
      discoveryContentItems,
      loadDiscoveryContentItems,
      addDiscoveryContentItem,
      getDiscoveryContentItem,
      removeDiscoveryContentItem,
      updateDiscoveryContentItem
    ]
  );
  return (
    <DiscoveryContentItemsContext.Provider value={providerValue}>{children}</DiscoveryContentItemsContext.Provider>
  );
};

const useDiscoveryContentItemsContext = () => {
  const context = useContext(DiscoveryContentItemsContext);
  if (!context) {
    throw new Error(`DiscoveryContentItemsContext must be used within the DiscoveryContentItemsProvider component`);
  }
  return context;
};

export { DiscoveryContentItemsProvider as default, useDiscoveryContentItemsContext };
